function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

// Shared by any auto-scrolling component (announcement bar, carousel, scrolling text) that needs
// to pause while off-screen and resume when it re-enters the viewport.
function watchViewportEnter(element, callback, { skipInDesignMode = false } = {}) {
  element.observer && element.observer.disconnect();
  element.observer = new IntersectionObserver(function(entries) {
    if(entries[0].isIntersecting === true) {
      callback();
    }
  }, { threshold: [0] });
  if(skipInDesignMode && Shopify.designMode) {
    return;
  }
  element.observer.observe(element);
}

function watchViewportLeave(element, callback) {
  element.observer && element.observer.disconnect();
  element.observer = new IntersectionObserver(function(entries) {
    if(entries[0].isIntersecting !== true) {
      callback();
    }
  }, { threshold: [0] });
  element.observer.observe(element);
}

// Shared swipe-to-navigate touch handling for announcement bar / carousel.
// Returns a teardown function so callers can remove the listeners in disconnectedCallback.
function setupHorizontalSwipe(element, { onSwipeNext, onSwipePrev }) {
  let startScreenX;
  const getTouch = (e) => {
    const evt = e.originalEvent === undefined ? e : e.originalEvent;
    return evt.touches[0] || evt.changedTouches[0];
  };
  const onTouchStart = (e) => {
    startScreenX = getTouch(e).screenX;
  };
  const onTouchEnd = (e) => {
    const screenX = getTouch(e).screenX;
    if(screenX < startScreenX && (startScreenX - screenX) > 40) {
      onSwipeNext();
    }
    else if(screenX > startScreenX && (screenX - startScreenX) > 40) {
      onSwipePrev();
    }
  };
  element.addEventListener('touchstart', onTouchStart, { passive: false });
  element.addEventListener('touchend', onTouchEnd, { passive: false });
  return () => {
    element.removeEventListener('touchstart', onTouchStart);
    element.removeEventListener('touchend', onTouchEnd);
  };
}

class CurrencySelector extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if(!this.form) {
      throw new Error('No localization form found.');
    }
    if(!this.querySelector('input[name="currency"]')) {
      const input = document.createElement('input');
      input.setAttribute('name', 'currency');
      input.setAttribute('type', 'hidden');
      this.form.append(input)
    }
    const codes = this.querySelectorAll('[data-currency-code]');
    codes.forEach(i => {
      i.addEventListener('click', this.selectCurrency.bind(this));
    })
  }

  get form() {
    return this.querySelector('form');
  }

  get input() {
    return this.querySelector('input[name="currency"]');
  }

  selectCurrency(e) {
    const code = e.target.dataset.currencyCode;
    this.input.value = code;
    this.form.submit();
  }
}

customElements.define('currency-selector', CurrencySelector);

class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));

    this.summaryToggle.setAttribute('role', 'button');
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
  }
}

customElements.define('details-modal', DetailsModal);

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function calcHeight(element, container) {
  const clone = element.cloneNode(true);
  Object.assign(clone.style, {
    overflow: 'visible',
    height: 'auto',
    maxHeight: 'none',
    opacity: '0',
    visibility: 'hidden',
    display: 'block'
  });
  container.after(clone);
  const height = clone.offsetHeight;
  clone.remove();
  return height;
}

function horizontalLoop(items, config) {
  let timeline;
  items = gsap.utils.toArray(items);
  config = config || {};
  gsap.context(() => { // use a context so that if this is called from within another context or a gsap.matchMedia(), we can perform proper cleanup like the "resize" event handler on the window
    let onChange = config.onChange,
      lastIndex = 0,
      tl = gsap.timeline({repeat: config.repeat, onUpdate: onChange && function() {
          let i = tl.closestIndex();
          if (lastIndex !== i) {
            lastIndex = i;
            onChange(items[i], i);
          }
        }, paused: config.paused, defaults: {ease: "none"}, onReverseComplete: () => tl.totalTime(tl.rawTime() + tl.duration() * 100)}),
      length = items.length,
      startX = items[0].offsetLeft,
      times = [],
      widths = [],
      spaceBefore = [],
      xPercents = [],
      curIndex = 0,
      indexIsDirty = false,
      center = config.center,
      pixelsPerSecond = (config.speed || 1) * 100,
      snap = config.snap === false ? v => v : gsap.utils.snap(config.snap || 1), // some browsers shift by a pixel to accommodate flex layouts, so for example if width is 20% the first element's width might be 242px, and the next 243px, alternating back and forth. So we snap to 5 percentage points to make things look more natural
      timeOffset = 0,
      container = center === true ? items[0].parentNode : gsap.utils.toArray(center)[0] || items[0].parentNode,
      totalWidth,
      getTotalWidth = () => items[length-1].offsetLeft + xPercents[length-1] / 100 * widths[length-1] - startX + spaceBefore[0] + items[length-1].offsetWidth * gsap.getProperty(items[length-1], "scaleX") + (parseFloat(config.paddingRight) || 0),
      populateWidths = () => {
        let b1 = container.getBoundingClientRect(), b2;
        items.forEach((el, i) => {
          widths[i] = parseFloat(gsap.getProperty(el, "width", "px"));
          xPercents[i] = snap(parseFloat(gsap.getProperty(el, "x", "px")) / widths[i] * 100 + gsap.getProperty(el, "xPercent"));
          b2 = el.getBoundingClientRect();
          spaceBefore[i] = b2.left - (i ? b1.right : b1.left);
          b1 = b2;
        });
        gsap.set(items, { // convert "x" to "xPercent" to make things responsive, and populate the widths/xPercents Arrays to make lookups faster.
          xPercent: i => xPercents[i]
        });
        totalWidth = getTotalWidth();
      },
      timeWrap,
      populateOffsets = () => {
        timeOffset = center ? tl.duration() * (container.offsetWidth / 2) / totalWidth : 0;
        center && times.forEach((t, i) => {
          times[i] = timeWrap(tl.labels["label" + i] + tl.duration() * widths[i] / 2 / totalWidth - timeOffset);
        });
      },
      getClosest = (values, value, wrap) => {
        let i = values.length,
          closest = 1e10,
          index = 0, d;
        while (i--) {
          d = Math.abs(values[i] - value);
          if (d > wrap / 2) {
            d = wrap - d;
          }
          if (d < closest) {
            closest = d;
            index = i;
          }
        }
        return index;
      },
      populateTimeline = () => {
        let i, item, curX, distanceToStart, distanceToLoop;
        tl.clear();
        for (i = 0; i < length; i++) {
          item = items[i];
          curX = xPercents[i] / 100 * widths[i];
          distanceToStart = item.offsetLeft + curX - startX + spaceBefore[0];
          distanceToLoop = distanceToStart + widths[i] * gsap.getProperty(item, "scaleX");
          tl.to(item, {xPercent: snap((curX - distanceToLoop) / widths[i] * 100), duration: distanceToLoop / pixelsPerSecond}, 0)
            .fromTo(item, {xPercent: snap((curX - distanceToLoop + totalWidth) / widths[i] * 100)}, {xPercent: xPercents[i], duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond, immediateRender: false}, distanceToLoop / pixelsPerSecond)
            .add("label" + i, distanceToStart / pixelsPerSecond);
          times[i] = distanceToStart / pixelsPerSecond;
        }
        timeWrap = gsap.utils.wrap(0, tl.duration());
      },
      refresh = (deep) => {
        let progress = tl.progress();
        tl.progress(0, true);
        populateWidths();
        deep && populateTimeline();
        populateOffsets();
        deep && tl.draggable ? tl.time(times[curIndex], true) : tl.progress(progress, true);
      },
      onResize = () => !config.disable_resize && refresh(true);
    gsap.set(items, {x: 0});
    populateWidths();
    populateTimeline();
    populateOffsets();
    window.addEventListener("resize", onResize);
    function toIndex(index, vars) {
      vars = vars || {};
      (Math.abs(index - curIndex) > length / 2) && (index += index > curIndex ? -length : length); // always go in the shortest direction
      let newIndex = gsap.utils.wrap(0, length, index),
        time = times[newIndex];
      if (time > tl.time() !== index > curIndex && index !== curIndex) { // if we're wrapping the timeline's playhead, make the proper adjustments
        time += tl.duration() * (index > curIndex ? 1 : -1);
      }
      if (time < 0 || time > tl.duration()) {
        vars.modifiers = {time: timeWrap};
      }
      curIndex = newIndex;
      vars.overwrite = true;
      return vars.duration === 0 ? tl.time(timeWrap(time)) : tl.tweenTo(time, vars);
    }
    tl.toIndex = (index, vars) => toIndex(index, vars);
    tl.closestIndex = setCurrent => {
      let index = getClosest(times, tl.time(), tl.duration());
      if (setCurrent) {
        curIndex = index;
        indexIsDirty = false;
      }
      return index;
    };
    tl.current = () => indexIsDirty ? tl.closestIndex(true) : curIndex;
    tl.next = vars => toIndex(tl.current()+1, vars);
    tl.previous = vars => toIndex(tl.current()-1, vars);
    tl.times = times;
    tl.progress(1, true).progress(0, true); // pre-render for performance
    if (config.reversed) {
      tl.vars.onReverseComplete();
      tl.reverse();
    }
    tl.closestIndex(true);
    lastIndex = curIndex;
    onChange && onChange(items[curIndex], curIndex);
    timeline = tl;
    return () => window.removeEventListener("resize", onResize); // cleanup
  });
  return timeline;
}

function getSectionInnerHTML(html, selector) {
  return new DOMParser()
    .parseFromString(html, 'text/html')
    .querySelector(selector).innerHTML;
}

function pauseAllMedia(element = null) {
  if(element == null) {
    element = document;
  }
  element.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  element.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  element.querySelectorAll('video').forEach((video) => video.pause());
  element.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

class DeferredMedia extends HTMLElement {
  connectedCallback() {
    this.shouldAutoPlay = this.dataset.shouldAutoplay;
    if(this.shouldAutoPlay) {
      window.setTimeout(() => {
        this.loadContent(false);
      }, 700)
    }
    this.carousel = this.closest('carousel-block');
    if(this.carousel) {
      this.carousel.addEventListener('slide', this.toggleAutoPlay.bind(this))
    }
    this.poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (this.poster) {
      this.poster.addEventListener('click', this.loadContent.bind(this));
    }
  }

  toggleAutoPlay() {
    if(!this.deferredElement || this.deferredElement.nodeName != 'VIDEO') {
      return;
    }
    if(this.carousel.isVisible(this) && this.deferredElement.getAttribute('autoplay')) {
      this.deferredElement?.play();
    }
    else {
      this.deferredElement?.pause();
    }
  }

  loadContent(focus = true) {
    pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));
      this.setAttribute('loaded', true);
      this.poster.querySelector('img').classList.add('blur-md');
      this.deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) this.deferredElement.focus();
      if (this.deferredElement.nodeName == 'VIDEO' && this.deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        this.deferredElement.muted = true;
        this.deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

if (!customElements.get('share-button')) {
  customElements.define(
    'share-button',
    class ShareButton extends DetailsDisclosure {
      constructor() {
        super();

        this.elements = {
          shareButton: this.querySelector('button'),
          shareSummary: this.querySelector('summary'),
          closeButton: this.querySelector('.share-button__close'),
          successMessage: this.querySelector('[id^="ShareMessage"]'),
          urlInput: this.querySelector('input'),
        };
        this.urlToShare = this.elements.urlInput ? this.elements.urlInput.value : document.location.href;

        if (navigator.share) {
          this.mainDetailsToggle.setAttribute('hidden', '');
          this.elements.shareButton.classList.remove('hidden');
          this.elements.shareButton.addEventListener('click', () => {
            navigator.share({ url: this.urlToShare, title: document.title });
          });
        } else {
          this.mainDetailsToggle.addEventListener('toggle', this.toggleDetails.bind(this));
          this.mainDetailsToggle
            .querySelector('.share-button__copy')
            .addEventListener('click', this.copyToClipboard.bind(this));
          this.mainDetailsToggle.querySelector('.share-button__close').addEventListener('click', this.close.bind(this));
        }
      }

      toggleDetails() {
        if (!this.mainDetailsToggle.open) {
          this.elements.successMessage.classList.add('hidden');
          this.elements.successMessage.textContent = '';
          this.elements.closeButton.classList.add('hidden');
          this.elements.shareSummary.focus();
        }
      }

      copyToClipboard() {
        navigator.clipboard.writeText(this.elements.urlInput.value).then(() => {
          this.elements.successMessage.classList.remove('hidden');
          this.elements.successMessage.textContent = 'Link copied to clipboard';
          this.elements.closeButton.classList.remove('hidden');
          this.elements.closeButton.focus();
        });
      }

      updateUrl(url) {
        this.urlToShare = url;
        this.elements.urlInput.value = url;
      }
    }
  );
}

const waitForImageToLoad = (imageElement) => {
  return new Promise(resolve=> {
    const src = imageElement.src;
    imageElement.onload = resolve
    imageElement.src = src;
  })
}

const resizeHeadings = () => {
  const headings = document.querySelectorAll('.heading-block');
  headings.forEach(i => {
    i.classList.remove('shrink-required');
    const headingWidth = i.parentElement.scrollWidth;
    const paddingMobile = 48;
    const pageWidth = window.innerWidth - paddingMobile;
    const shrinkFactor = pageWidth / headingWidth;
    if(shrinkFactor < 1) {
      const textSize = parseFloat(window.getComputedStyle(i).getPropertyValue('font-size'));
      i.style.setProperty("--shrink-text", `${Math.floor(textSize * shrinkFactor)}px`);
      i.classList.add('shrink-required')
    }
  })
}

const setupPayNowButtons = () => {
  const wrappers = document.querySelectorAll('.payment-button-wrapper');
  wrappers.forEach(i => {
    const template = i.querySelector('.button-template');
    if(!template) {
      return;
    }
    const styles = window.getComputedStyle(template);
    i.querySelectorAll('button').forEach(b => {
      b.style.borderWidth = styles.borderWidth;
      b.style.borderStyle = styles.borderStyle;
      b.style.padding = styles.padding;
      b.style.borderRadius = styles.borderRadius;
      b.style.width = styles.width === '100%' ? '100%' : 'auto';
    });
    i.style.opacity = 100;
  })
}

// Button animations: "Splash" (Osmo Button 075) and "Sparkle" (Osmo Button 091).
// Listeners are delegated so buttons rendered later (section reloads, cart drawer) need no setup.
const BUTTON_SPARK_WIDTH = 6;
const BUTTON_ANIMATION_COOLDOWN = 250;
const BUTTON_SPARKLE_BURSTS = 3;
const BUTTON_SPARKLE_DISTANCE = 24;
const BUTTON_SPARKLE_STAGGER = 80;
const buttonReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const buttonFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

const pxToEm = (px) => `${px / 16}em`;

function createButtonSpark(container, x, y, keyframes, duration) {
  const spark = document.createElement('span');
  spark.className = 'button-spark';
  spark.style.left = `${x}px`;
  spark.style.top = `${y}px`;
  spark.setAttribute('aria-hidden', 'true');
  container.appendChild(spark);
  spark.animate(keyframes, { duration, easing: 'ease-out', fill: 'both' }).finished
    .catch(() => {})
    .finally(() => spark.remove());
}

// Box of the button label relative to the spark layer, falling back to the padded content area
function getButtonTextBox(button, place) {
  const placeRect = place.getBoundingClientRect();
  const text = button.querySelector('[data-button-animation-text]');
  let rect;
  if(text) {
    rect = text.getBoundingClientRect();
  }
  else {
    const inner = button.querySelector('[data-button-animation-inner]');
    const styles = window.getComputedStyle(inner);
    const innerRect = inner.getBoundingClientRect();
    const left = innerRect.left + parseFloat(styles.paddingLeft) + parseFloat(styles.borderLeftWidth);
    const right = innerRect.right - parseFloat(styles.paddingRight) - parseFloat(styles.borderRightWidth);
    rect = { left, width: right - left, top: innerRect.top, height: innerRect.height };
  }
  return {
    x: rect.left - placeRect.left,
    y: rect.top - placeRect.top,
    width: rect.width,
    height: rect.height
  };
}

function playButtonSplash(button, place) {
  const box = getButtonTextBox(button, place);
  const y = box.y + box.height / 2;
  const bursts = [
    { x: box.x - 14, directionX: 3, sparks: [[140, 0, 8], [180, -6, 0], [220, 0, -8]] },
    { x: box.x + box.width + 14, directionX: -3, sparks: [[-40, 0, -8], [0, 6, 0], [40, 0, 8]] }
  ];
  bursts.forEach(({ x, directionX, sparks }) => {
    sparks.forEach(([rotation, offsetX, offsetY]) => {
      const start = `translate(-50%, -50%) rotate(${rotation}deg) translate(${pxToEm(BUTTON_SPARK_WIDTH / 2)}, 0)`;
      const middle = `${start} translate(${pxToEm(9)}, 0) scale(0.5, 0.75)`;
      const end = `${middle} translate(${pxToEm(BUTTON_SPARK_WIDTH / 2)}, 0) scale(0, 0)`;
      createButtonSpark(place, x + offsetX, y + offsetY, [
        { offset: 0, transform: start, translate: `${pxToEm(directionX)} 0` },
        { offset: 0.65, transform: middle },
        { offset: 1, transform: end, translate: '0 0' }
      ], 500);
    });
  });
}

// Spread burst points across a grid (extended past the button edges) so they don't bunch up
function getButtonSparklePoints(place, amount) {
  const width = place.offsetWidth + BUTTON_SPARKLE_DISTANCE * 2;
  const height = place.offsetHeight + BUTTON_SPARKLE_DISTANCE * 2;
  const cols = 2;
  const rows = amount <= 2 ? 1 : amount <= 4 ? 2 : 3;
  const cellWidth = width / cols;
  const cellHeight = height / rows;
  const cells = [];
  for(let row = 0; row < rows; row++) {
    for(let col = 0; col < cols; col++) {
      cells.push({ row, col });
    }
  }
  const random = (min, max) => min + Math.random() * (max - min);
  return cells
    .sort(() => Math.random() - 0.5)
    .slice(0, amount)
    .map(({ row, col }) => {
      const cellX = col * cellWidth - BUTTON_SPARKLE_DISTANCE;
      const cellY = row * cellHeight - BUTTON_SPARKLE_DISTANCE;
      return {
        x: random(cellX + cellWidth * 0.25, cellX + cellWidth * 0.75),
        y: random(cellY + cellHeight * 0.25, cellY + cellHeight * 0.75)
      };
    });
}

function playButtonSparkle(button, place) {
  getButtonSparklePoints(place, BUTTON_SPARKLE_BURSTS).forEach((point, index) => {
    setTimeout(() => {
      [-90, -180, -270, -360].forEach((rotation) => {
        const start = `translate(-50%, -50%) rotate(${rotation}deg) translate(${pxToEm(1)}, 0)`;
        const middle = `${start} translate(${pxToEm(10)}, 0) scale(0.5, 0.75)`;
        const end = `${middle} translate(${pxToEm(BUTTON_SPARK_WIDTH / 2)}, 0) scale(0, 0)`;
        createButtonSpark(place, point.x, point.y, [
          { offset: 0, transform: start, translate: `0 ${pxToEm(6)}` },
          { offset: 0.65, transform: middle },
          { offset: 1, transform: end, translate: '0 0' }
        ], 450);
      });
    }, index * BUTTON_SPARKLE_STAGGER);
  });
}

function playButtonAnimation(button) {
  const place = button.querySelector('[data-button-animation-place]');
  if(!place || button.buttonAnimationCooldown || buttonReducedMotion.matches || button.matches(':disabled, [aria-disabled="true"]')) {
    return;
  }
  if(button.dataset.buttonAnimation === 'splash') {
    playButtonSplash(button, place);
  }
  else if(button.dataset.buttonAnimation === 'sparkle') {
    playButtonSparkle(button, place);
  }
  button.buttonAnimationCooldown = true;
  setTimeout(() => {
    button.buttonAnimationCooldown = false;
  }, BUTTON_ANIMATION_COOLDOWN);
}

document.addEventListener('pointerover', (event) => {
  const button = event.target.closest?.('[data-button-animation]');
  if(!button || !buttonFinePointer.matches || button.contains(event.relatedTarget)) {
    return;
  }
  playButtonAnimation(button);
});

// Splash also fires on tap, as touch devices never get the hover
document.addEventListener('pointerdown', (event) => {
  const button = event.target.closest?.('[data-button-animation="splash"]');
  if(button) {
    playButtonAnimation(button);
  }
});

document.addEventListener('focusin', (event) => {
  const button = event.target;
  if(button.matches?.('[data-button-animation]:focus-visible')) {
    playButtonAnimation(button);
  }
});

window.onload = () => {
  resizeHeadings();
  window.addEventListener('resize', debounce(() => resizeHeadings(), 100));
  setupPayNowButtons();
}